import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import crmDemoService, { type CrmDemo, type CrmDemoInput, type CrmDemoListFilters } from '@/services/crmDemo.service';
import { invalidateCrmActivityViews } from '@/hooks/useCrmActivities';

export const CRM_DEMO_KEYS = {
  all: ['crm', 'demos'] as const,
  list: (filters: CrmDemoListFilters) => [...CRM_DEMO_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...CRM_DEMO_KEYS.all, 'detail', id] as const,
};

function errMsg(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { message?: string } }; message?: string };
  return r?.response?.data?.message || r?.message || fallback;
}

export function useCrmDemos(filters: CrmDemoListFilters) {
  return useQuery({ queryKey: CRM_DEMO_KEYS.list(filters), queryFn: () => crmDemoService.list(filters) });
}

export function useCrmDemo(id: string | undefined) {
  return useQuery({
    queryKey: CRM_DEMO_KEYS.detail(id ?? ''),
    queryFn: () => crmDemoService.getById(id!),
    enabled: !!id,
  });
}

/** Demo writes also create / close follow-ups and activities, so refresh those views too. */
function useDemoMutation<V>(fn: (v: V) => Promise<CrmDemo>, success: string, failure: string) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: fn,
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: CRM_DEMO_KEYS.all });
      invalidateCrmActivityViews(qc, {
        lead_id: row?.lead_id,
        opportunity_id: row?.opportunity_id,
        customer_id: row?.customer_id,
      });
      message.success(success);
    },
    onError: (e) => message.error(errMsg(e, failure)),
  });
}

export function useCreateCrmDemo() {
  return useDemoMutation((body: CrmDemoInput) => crmDemoService.create(body), 'Demo created', 'Failed to create demo');
}

export function useUpdateCrmDemo() {
  return useDemoMutation(
    ({ id, body }: { id: string; body: CrmDemoInput }) => crmDemoService.update(id, body),
    'Demo saved',
    'Failed to save demo'
  );
}

export function useCompleteCrmDemo() {
  return useDemoMutation(
    ({ id, body }: { id: string; body: Parameters<typeof crmDemoService.complete>[1] }) =>
      crmDemoService.complete(id, body),
    'Demo completed and logged as an activity',
    'Failed to complete demo'
  );
}

export function useCancelCrmDemo() {
  return useDemoMutation(
    ({ id, reason }: { id: string; reason: string }) => crmDemoService.cancel(id, reason),
    'Demo cancelled',
    'Failed to cancel demo'
  );
}
